import { SupabaseClient } from '@supabase/supabase-js';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { v4 } from 'uuid';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { Base } from '../../models/base.model';

import { ICrud } from './crud.interface';
import { Database } from '../../models/types';
import { SupabaseService } from './supabase.service';
import { inject } from '@angular/core';
import { TextFilterConfig } from 'src/app/shared/components/smart/table/table.component';

export class DatabaseService<T extends Base> implements ICrud<T> {
  protected table: string;
  // private supabaseUrl: string;
  // private supabaseKey: string;
  private sbs: SupabaseService = inject(SupabaseService);
  // Http client used for calling lambda endpoints when configured
  protected httpIntern: HttpClient = inject(HttpClient);
  public supabase: SupabaseClient<Database>;

  /**
   * Optional configuration that subclasses can set to route write
   * operations through lambdas instead of using the Supabase client
   * directly.
   * Example: in CommunicationService set
   * this.lambdaNames = { create: 'create_thread', update: 'update_thread' }
   */
  protected lambdaNames?: { create?: string; update?: string; delete?: string };

  public _getPagination = (page: number, size: number) => {
    const limit = size ? +size : 3;
    const from = page ? page * limit : 0;
    const to = page ? from + size - 1 : size - 1;
    return { from, to };
  };

  constructor(table: string) {
    this.table = table;
    this.supabase = this.sbs.supabase;
  }

  async get(id: string): Promise<T> {
    const { data, error } = await this.supabase
      .from(this.table)
      .select()
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);

    return data;
  }

  async getAll(limit: number, page: number): Promise<T[]> {
    const query = this.supabase.from(this.table).select();
    if (limit) query.limit(limit);
    if (page) {
      const { from, to } = this._getPagination(page, limit);
      query.range(from, to);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return data;
  }

  // async getAllDistinct(column: string): Promise<any[]> {
  //   const { data, error } = await this.supabase
  //     .rpc('distinct_values', { schema: 'public', table: this.table, column })
  //     .select('distinct_values');

  //   if (error) {
  //     throw new Error(error.message);
  //   }

  //   return data?.map((item: any) => item.distinct_values);
  // }

  async create(t: Partial<T>) {
    // If a lambda is configured for create operations, call it instead
    if (this.lambdaNames && this.lambdaNames.create) {
      const res = await this.callLambda(this.lambdaNames.create, 'POST', t);
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .insert(t)
      .select();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  }

  async update(t: Partial<T>) {
    if (this.lambdaNames && this.lambdaNames.update) {
      const res = await this.callLambda(this.lambdaNames.update, 'PUT', {
        id: t.id,
        patch: t,
      });
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .update(t)
      .eq('id', t.id)
      .select();
    if (error) {
      console.error('UPDATE ERROR', error);
      throw new Error(error.message);
    }
    return data;
  }

  async delete(t: T) {
    console.log('DELETE', t);
    console.log('DELETE', this.table);
    if (this.lambdaNames && this.lambdaNames.delete) {
      const res = await this.callLambda(
        this.lambdaNames.delete,
        'DELETE',
        undefined,
        String(t.id)
      );
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .delete()
      .eq('id', t.id);
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }

  // Protected helper to call a lambda HTTP endpoint configured in serverless
  protected async callLambda(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: any,
    pathParam?: string
  ): Promise<any> {
    const base = (environment.lambdaUrl || '').replace(/\/$/, '');
    const url = pathParam
      ? `${base}/${endpoint}/${encodeURIComponent(pathParam)}`
      : `${base}/${endpoint}`;

    // try to obtain the current user's access token from the supabase client
    let token: string | null = null;
    try {
      // supabase-js v2
      // @ts-ignore
      const sess = await this.supabase.auth.getSession();
      token = sess?.data?.session?.access_token ?? null;
    } catch (e) {
      // ignore and proceed without token
      token = null;
    }

    const headersObj: any = { 'Content-Type': 'application/json' };
    if (environment.supabaseKey) headersObj['apikey'] = environment.supabaseKey;
    if (token) headersObj['Authorization'] = `Bearer ${token}`;

    const headers = new HttpHeaders(headersObj);

    try {
      const obs = this.httpIntern.request(method, url, { body, headers });
      const resp = await lastValueFrom(obs as any);
      return resp;
    } catch (err: any) {
      console.error('Lambda call error', endpoint, err);
      throw err;
    }
  }

  list(
    pageIndex: number = 0,
    pageSize: number = 5,
    filters: Filter[] = [],
    sort: Sort[] = [],
    search?: TextFilterConfig
  ): Observable<QueryResult<T>> {
    const subj = new Subject<QueryResult<T>>();

    // If there's a `contains` filter on the id column, we cannot call ILIKE on uuid
    // via the REST filters. Instead call an RPC that searches UUIDs as text and
    // convert the result into an `in` or `eq` filter. Keep everything async via
    // promise chains so this method remains synchronous (returns Observable).

    // detect filters on id that use text-matching operators which cannot be
    // applied directly to uuid columns via REST (ilike). We handle these by
    // resolving matching UUIDs via an RPC and converting to eq/in filters.
    const idContainsIndex = filters.findIndex(
      (f) =>
        f.column === 'id' &&
        (f.operator === 'contains' ||
          f.operator === 'startswith' ||
          f.operator === 'endswith')
    );

    // Use object-oriented helpers instead of an inline function
    const run = (effectiveFilters: Filter[], pageIdx: number, pageSz: number) =>
      this.runQueryAndEmit(
        effectiveFilters,
        pageIdx,
        pageSz,
        sort,
        search,
        subj
      );

    if (idContainsIndex === -1) {
      // No special id contains filter: build and run normally
      run(filters, pageIndex, pageSize);
    } else {
      // Extract the id filter and resolve matching UUIDs, then run
      const idFilter = filters[idContainsIndex];

      this.resolveUuidFilters(idFilter, filters, idContainsIndex)
        .then((newFilters) => run(newFilters, pageIndex, pageSize))
        .catch((err) => {
          console.error('Error resolving uuid filters:', err);
          // on error, force empty result set
          const newFilters = filters.slice();
          newFilters.splice(idContainsIndex, 1);
          newFilters.push({
            column: 'id',
            operator: 'eq',
            value: '00000000-0000-0000-0000-000000000000',
            visible: false,
          });
          run(newFilters, pageIndex, pageSize);
        });
    }
    return subj.asObservable();
  }

  // Build the supabase query applying filters, pagination, sorting and search
  private buildQuery(
    effectiveFilters: Filter[],
    pageIndex: number,
    pageSize: number,
    sort: Sort[],
    search?: TextFilterConfig
  ) {
    let q: any = this.supabase
      .from(this.table)
      .select('*', { count: 'estimated' });

    for (const filter of effectiveFilters) {
      q = applyFilter(q, filter);
    }

    q = q.range(pageIndex * pageSize, (pageIndex + 1) * pageSize - 1);

    for (const s of sort) {
      q = q.order(s.column, { ascending: s.direction === 'asc' });
    }

    if (search) {
      q = q.textSearch(search.column, search.value, search.options);
    }

    return q;
  }

  // Run a built query and emit result to the provided Subject
  private runQueryAndEmit(
    effectiveFilters: Filter[],
    pageIndex: number,
    pageSize: number,
    sort: Sort[],
    search: TextFilterConfig | undefined,
    subj: Subject<QueryResult<T>>
  ) {
    const q = this.buildQuery(
      effectiveFilters,
      pageIndex,
      pageSize,
      sort,
      search
    );
    q.then((ev: any) => {
      subj.next({ data: ev.data as T[], count: ev.count ?? 0 });
    });
  }

  // Resolve filters that target uuid columns (id) using the RPC and optional
  // client-side refinements. Returns a new filters array where the original id
  // text-matching filter is replaced by an eq/in (or an always-empty eq) filter.
  private async resolveUuidFilters(
    idFilter: Filter,
    allFilters: Filter[],
    idIndex: number
  ): Promise<Filter[]> {
    const newFilters = allFilters.slice();
    // remove the original id filter
    newFilters.splice(idIndex, 1);

    try {
      const res: any = await (this.supabase as any).rpc('search_uuid_text', {
        search_term: idFilter.value,
        table_name: this.table,
      });

      if (res?.error) {
        console.error('RPC search_uuid_text error:', res.error);
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: '00000000-0000-0000-0000-000000000000',
          visible: false,
        });
        return newFilters;
      }

      const rows: any[] = Array.isArray(res?.data) ? res.data : [];
      let filteredRows = rows;

      // RPC always does ILIKE '%term%'. If caller wanted startswith/endswith,
      // refine client-side.
      if (idFilter.operator === 'startswith') {
        filteredRows = rows.filter((r: any) =>
          String(r.uuid_result).startsWith(String(idFilter.value))
        );
      } else if (idFilter.operator === 'endswith') {
        filteredRows = rows.filter((r: any) =>
          String(r.uuid_result).endsWith(String(idFilter.value))
        );
      }

      const ids: string[] = filteredRows
        .map((r: any) => r.uuid_result)
        .filter(Boolean);

      if (ids.length === 0) {
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: '00000000-0000-0000-0000-000000000000',
          visible: false,
        });
      } else if (ids.length === 1) {
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: ids[0],
          visible: false,
        });
      } else {
        newFilters.push({
          column: 'id',
          operator: 'in',
          value: ids,
          visible: false,
        });
      }

      return newFilters;
    } catch (err) {
      console.error('Error mapping RPC result to ids:', err);
      newFilters.push({
        column: 'id',
        operator: 'eq',
        value: '00000000-0000-0000-0000-000000000000',
        visible: false,
      });
      return newFilters;
    }
  }

  count(filters: Filter[] = []): Observable<QueryResult<T>> {
    const subj = new Subject<QueryResult<T>>();

    let a = this.supabase
      .from(this.table)
      .select('*', { count: 'exact', head: true });

    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];
      a = applyFilter(a, filter);
    }

    a.then((ev) => {
      subj.next({ data: ev.data as T[], count: ev.count! });
    });

    return subj.asObservable();
  }

  async getAllDistinct(column: string) {
    const data = await this.supabase
      .from(this.table)
      .select(`${column}.count()`, { count: 'exact' });

    if (data.error) {
      throw new Error(data.error.message);
    }

    return data;
  }
}

export interface QueryResult<T> {
  data: T[];
  count: number;
}
export interface Sort {
  column: string;
  direction: 'asc' | 'desc';
}
export interface Filter {
  column: string;
  operator:
    | 'gte'
    | 'gt'
    | 'eq'
    | 'lt'
    | 'lte'
    | 'ilike'
    | 'neq'
    | 'contains'
    | 'empty'
    | 'in'
    // | 'ncontains'
    | 'startswith'
    | 'endswith';
  value: string | number | boolean | any[] | null;
  visible: boolean;
}

function applyFilter(current: any, filter: Filter): any {
  if (filter.value === null || filter.value === undefined) return current;

  try {
    switch (filter.operator) {
      case 'contains':
        return current.ilike(filter.column, `%${filter.value}%`);
      case 'startswith':
        return current.ilike(filter.column, `${filter.value}%`);
      case 'endswith':
        return current.ilike(filter.column, `%${filter.value}`);
      case 'ilike':
        return current.ilike(filter.column, String(filter.value));
      case 'empty':
        return current.is(filter.column, null);
      case 'eq':
        return current.eq(filter.column, filter.value);
      case 'neq':
        return current.neq(filter.column, filter.value);
      case 'gte':
        return current.gte(filter.column, filter.value);
      case 'lte':
        return current.lte(filter.column, filter.value);
      case 'gt':
        return current.gt(filter.column, filter.value);
      case 'lt':
        return current.lt(filter.column, filter.value);
      case 'in':
        if (Array.isArray(filter.value) && filter.value.length > 0) {
          return current.in(filter.column, filter.value as any[]);
        }
        // empty array -> no-op (don't apply filter)
        return current;
      default:
        // fallback: try to call the operator dynamically if available
        if (typeof current[filter.operator] === 'function') {
          return current[filter.operator](filter.column, filter.value);
        }
        console.warn('Unsupported filter operator', filter.operator);
        return current;
    }
  } catch (e) {
    console.warn('applyFilter error', e);
    return current;
  }
}
